import React, { useEffect } from 'react'
import { Form } from 'antd';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { api } from '../../../../../api/api';
import { DirectionGroupObj, DirectionObj, ObjDB } from '../../../../../types/apiTypes';
import FormStatus from '../../../../../components/form/FormStatus/FormStatus';
import { useFormStatus } from '../../../../../utils/hooks/useFormStatus';
import { DirectionFormFields } from '../../../../../types/formFieldTypes';
import EditPlaceholder from '../../../../../components/common/EditPlaceholder/EditPlaceholder';
import { getAntField } from '../../../../../utils/form';
import DirectionFormInputs from '../DirectionFormInputs/DirectionFormInputs';
import { clearDirectionErrors, directionValidate, initialDirectionFields } from '../../formData/DirectionFormData';
import { invalidateQueries } from '../../../../../utils/rest';
import { queryKeysDirection } from '../../../../../static/data/queryKeys';

type DirectionEditFormProps = {
    editItem: ObjDB<DirectionObj> | null
    directionGroups: ObjDB<DirectionGroupObj>[]
    setOpenedForm: React.Dispatch<React.SetStateAction<boolean>>
    setEditItem: React.Dispatch<React.SetStateAction<ObjDB<DirectionObj> | null>>
}

const DirectionEditForm: React.FC<React.PropsWithChildren<DirectionEditFormProps>> = React.memo((props) => {
    const {
        editItem, setOpenedForm, setEditItem,
        directionGroups,
    } = props

    const formStatus = useFormStatus()

    const {
        errors, values, touched,
        handleChange, setFieldValue,
        submitForm, setValues, setErrors,
    } = useFormik<DirectionFormFields>({
        initialValues: initialDirectionFields,
        onSubmit: (values) => {
            mutate(values)
        },
        validate: directionValidate
    })

    const antField = getAntField<DirectionFormFields>(values, errors, touched)

    const {
        mutate
    } = useMutation(
        async (values: DirectionFormFields) => {
            if (editItem) return await api.directions.edit(values, editItem)
        },
        {
            mutationKey: 'direction-edit',
            onSuccess() {
                invalidateQueries(queryKeysDirection[1])
                formStatus.setMessage('Данные успешно изменены')
                formStatus.setStatus('success')
                setTimeout(clearEditItem, 1000)
            },
            onError(err: Error) {
                console.error('ERROR', err)
                formStatus.setMessage(`Ошибка: ${err.message}`)
                formStatus.setStatus('error')
                setTimeout(clearEditItem, 5000)
            },
            onMutate() {
                formStatus.setMessage('Отправка данных')
                formStatus.setStatus('fetching')
            }
        }
    )

    const clearForm = () => {
        setValues(initialDirectionFields)
        setErrors(clearDirectionErrors)
    }

    const clearEditItem = () => {
        clearForm()
        setOpenedForm(false)
        setTimeout(() => {
            setEditItem(null)
            formStatus.reset()
        }, 300)
    }

    const resetChanges = () => {
        if (editItem) {
            const { id, ...values } = editItem
            setValues(values)
            setErrors(clearDirectionErrors)
        }
    }

    useEffect(() => {
        if (editItem) return resetChanges()
        clearForm()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editItem])


    if (!editItem) return <EditPlaceholder />

    return (
        <Form
            fields={[
                antField('name'),
                antField('group_directions', { withFalsy: true }),
            ]}
        >
            {
                editItem &&
                <p className="uif-ant-form-title">
                    ID редактируемого объекта: <span>{editItem.id}</span>
                </p>
            }

            <DirectionFormInputs
                directionGroups={directionGroups}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                resetChanges={resetChanges}
                submitForm={submitForm}
            />

            <FormStatus formStatus={formStatus} />
        </Form>
    )
})

export default DirectionEditForm